import { Injectable } from '@angular/core';

interface TimeFrame {
  name: string;
  label: string;
}

interface RuleType {
  label: string;
  name: string;
}

interface ReasonType {
  label: string;
  name: string;
}

interface SalesRulesLimits {
  [eventId: string]: {
    items: any[];
  };
}

/**
 * SalesRulesService (Angular 18)
 *
 * Replaces: apps/b2b-ui/common/services/salesRules/salesRules.service.js
 * Complex service for managing sales rules, ticket limits, and group limits.
 * Handles member benefits and event sales rule enforcement.
 *
 * NOTE: This is a large service (1091 LOC) with 31 methods handling:
 * - Ticket limit validation
 * - Group limit computation
 * - Sales rule violation tracking
 * - Time frame and rule type configuration
 *
 * Due to size constraints, implementation is split into multiple sections.
 * See the file continuation for all 31 methods.
 */
@Injectable({ providedIn: 'root' })
export class SalesRulesService {
  limits: SalesRulesLimits = {};

  readonly timeFrames: Record<string, TimeFrame> = {
    day: { name: 'day', label: 'per day' },
    week: { name: 'week', label: 'per week' },
    month: { name: 'month', label: 'per month' },
    year: { name: 'year', label: 'per year' },
    event: { name: 'lifetime', label: 'per event' },
  };

  readonly ruleTypes: RuleType[] = [
    { label: 'Member', name: 'memberBenefits' },
    { label: 'Event', name: 'eventSales' },
  ];

  readonly reasonTypes: ReasonType[] = [
    { label: 'Cart', name: 'Cart' },
    { label: 'Transaction', name: 'Transaction' },
  ];

  ticketsQuantity: Record<string, number> = {};

  // ─── Initialization ───────────────────────────────────────────────────────

  init(): boolean {
    return true;
  }

  resetLimits(): void {
    this.limits = {};
  }

  // ─── Helper Methods ───────────────────────────────────────────────────────

  hasSalesRules(event: any): boolean {
    return !!(
      event &&
      Object.prototype.hasOwnProperty.call(event, 'ruleSets') &&
      Object.prototype.hasOwnProperty.call(event.ruleSets, 'associated') &&
      event.ruleSets.associated &&
      Object.prototype.hasOwnProperty.call(event.ruleSets, 'ruleSetIds') &&
      event.ruleSets.ruleSetIds instanceof Array
    );
  }

  findTimeFrame(timeFrame: string): TimeFrame | null {
    if (!timeFrame) return null;

    switch (timeFrame.toLowerCase()) {
      case this.timeFrames.day.name:
        return this.timeFrames.day;
      case this.timeFrames.week.name:
        return this.timeFrames.week;
      case this.timeFrames.month.name:
        return this.timeFrames.month;
      case this.timeFrames.year.name:
        return this.timeFrames.year;
      case this.timeFrames.event.name:
        return this.timeFrames.event;
    }

    return null;
  }

  // ─── Ticket collection ────────────────────────────────────────────────────

  getTicketsForLimits(event: any): any[] {
    const tickets: any[] = [];
    const prices = this.getPrices(event);

    if (prices instanceof Array) {
      return prices.map((price) => ({
        isRetail: price.isRetail,
        ticketTypeId: price.ticketingTypeId,
        name: price.ticketingTypeName,
        quantity: price.quantity,
      }));
    }

    return tickets;
  }

  getPrices(event: any): any[] {
    const r: any[] = [];
    if (event && event.tickets) {
      for (const ticket of event.tickets) {
        if (!ticket.isRetail) {
          r.push(ticket);
        }
      }
    }
    return r;
  }

  getGroupQuantity(event: any): Record<string, number> {
    const r: Record<string, number> = {};
    if (event && event.tickets) {
      for (const ticket of event.tickets) {
        if (!Object.prototype.hasOwnProperty.call(r, ticket.ticketingTypeId)) {
          r[ticket.ticketingTypeId] = ticket.quantity;
        } else {
          r[ticket.ticketingTypeId] += ticket.quantity;
        }
      }
    }
    return r;
  }

  getTicketsQuantities(tickets: any[]): Record<string, number> {
    const r: Record<string, number> = {};
    for (let i = 0; i < tickets.length; i++) {
      const ticketId = tickets[i].ticketingTypeId || tickets[i].ticketTypeId;
      if (!Object.prototype.hasOwnProperty.call(r, ticketId)) {
        r[ticketId] = parseInt(String(tickets[i].quantity), 10);
      } else {
        r[ticketId] += parseInt(String(tickets[i].quantity), 10);
      }
    }
    return r;
  }

  // ─── Quantity calculation ─────────────────────────────────────────────────

  getQuantityForTicketTypes(
    prices: any[],
    ticketTypeIds: string[],
    isRetail: boolean,
    sameDayEventTicketQuantities?: Record<string, any>,
    group?: any,
  ): number {
    let quantity = 0;

    if (prices instanceof Array) {
      for (const ticketTypeId of ticketTypeIds) {
        let r = 0;

        const found = prices.filter((ticket) => {
          const retail = !!ticket.isRetail;
          return ticket.ticketingTypeId === ticketTypeId && retail === isRetail;
        });

        if (found && found.length) {
          const key = isRetail ? found[0].ticketingTypeId + '-retail' : found[0].ticketingTypeId + '-regular';
          if (sameDayEventTicketQuantities && sameDayEventTicketQuantities[key] !== undefined) {
            r = sameDayEventTicketQuantities[key].quantity;
          } else {
            r = found[0].quantity;

            // calculate group quantity for prevents
            if (group && group.isPreventRule) {
              if (
                found[0].ticketingTypeId !== group.violation.ticketTypeId &&
                group.violation.ticketTypeIds.indexOf(found[0].ticketingTypeId) < 0
              ) {
                r = 0;
              }
            }
          }

          r = parseInt(String(r), 10);
          if (isNaN(r)) {
            r = 0;
          }
        }

        quantity += r;
      }
    }

    return quantity;
  }

  getQuantityForPreventTicketTypes(
    prices: any[],
    ticketTypeIds: string[],
    isRetail: boolean,
    sameDayEventTicketQuantities?: Record<string, any>,
    group?: any,
  ): number {
    let quantity = 0;

    if (prices instanceof Array) {
      for (const ticketTypeId of ticketTypeIds) {
        let r = 0;

        const found = prices.filter((ticket) => {
          const retail = !!ticket.isRetail;
          return ticket.ticketingTypeId === ticketTypeId && retail === isRetail;
        });

        if (found && found.length) {
          const key = isRetail ? found[0].ticketingTypeId + '-retail' : found[0].ticketingTypeId + '-regular';
          if (sameDayEventTicketQuantities && sameDayEventTicketQuantities[key] !== undefined) {
            r = sameDayEventTicketQuantities[key].quantity;
          } else {
            r = found[0].quantity;

            // calculate group quantity for prevented
            if (group && group.isPreventRule) {
              if (
                found[0].ticketingTypeId === group.violation.ticketTypeId ||
                group.violation.ticketTypeIds.indexOf(found[0].ticketingTypeId) >= 0
              ) {
                r = 0;
              }
            }
          }

          r = parseInt(String(r), 10);
          if (isNaN(r)) {
            r = 0;
          }
        }

        quantity += r;
      }
    }

    return quantity;
  }

  // ─── Update ticket limits ─────────────────────────────────────────────────

  updateTicketLimits(event: any, sameDayEventTicketQuantities?: Record<string, any>): void {
    const tickets = this.getTicketsForLimits(event);

    this.ticketsQuantity = this.getTicketsQuantities(tickets);

    event.memberDiscountedPriceMap = {};
    event.memberTicketLimitMap = {};
    event.memberGroupLimitMap = {};
    event.ticketLimitGroups = {};

    event.memberTicketLimitMap = this.getMemberTicketLimit(tickets, event.eventId);
    event.memberGroupLimitMap = this.getMemberGroupLimit(tickets, event.eventId);

    // Always build the per-ticket-type groups: getSameDayEventTicketQuantities() returns null for a
    // single-event cart, and checkLimitExceeded() needs these groups to flag combined-ticket-type
    // violations (DEV-14279).
    this.computeGroupTicketLimits(event, sameDayEventTicketQuantities);

    event.hasLimits =
      (event.memberTicketLimitMap && event.memberTicketLimitMap.hasLimits) ||
      (event.memberGroupLimitMap && event.memberGroupLimitMap.hasLimits);
    event.hasSalesRuleRestriction =
      (event.memberTicketLimitMap && event.memberTicketLimitMap.hasSalesRuleRestriction) ||
      (event.memberGroupLimitMap && event.memberGroupLimitMap.hasSalesRuleRestriction);
    event.hasLimitErrors = this.eventHasLimitErrors(event, sameDayEventTicketQuantities, false);
    event.hasSalesRuleErrors = this.eventHasLimitErrors(event, sameDayEventTicketQuantities, true);

    event.noLimitViolations = !event.hasLimits;
    event.noSalesRulesViolations = !event.hasSalesRuleRestriction;
  }

  // ─── Group ticket limits ──────────────────────────────────────────────────

  computeGroupTicketLimits(event: any, sameDayEventTicketQuantities?: Record<string, any>): void {
    const ticketTypeIds: string[] = [];

    // reset all ticket type group limits
    this.setMemberTicketLimitGroups(event, event.ticketLimitGroups, null, false);
    this.setMemberTicketLimitGroups(event, event.ticketLimitGroups, null, true);

    if (Object.prototype.hasOwnProperty.call(event.memberGroupLimitMap, 'regular')) {
      const groups = event.memberGroupLimitMap.regular;

      for (const group of groups) {
        let groupQuantity = 0;
        let preventGroupQuantity = 0;

        const groupIds = group.groupId
          .split('-')
          .filter((item: string, index: number, arr: string[]) => arr.indexOf(item) === index);

        if (!group.ruleType || group.ruleType.name === this.ruleTypes[0].name) {
          groupQuantity = this.getQuantityForTicketTypes(event.tickets, groupIds, false, sameDayEventTicketQuantities);
        } else {
          groupQuantity = this.getQuantityForTicketTypes(
            event.tickets,
            groupIds,
            false,
            sameDayEventTicketQuantities,
            group,
          );
          groupQuantity += this.getQuantityForTicketTypes(
            event.tickets,
            groupIds,
            true,
            sameDayEventTicketQuantities,
            group,
          );

          preventGroupQuantity = this.getQuantityForPreventTicketTypes(
            event.tickets,
            groupIds,
            false,
            sameDayEventTicketQuantities,
            group,
          );
          preventGroupQuantity += this.getQuantityForPreventTicketTypes(
            event.tickets,
            groupIds,
            true,
            sameDayEventTicketQuantities,
            group,
          );
        }

        for (const ticketTypeId of groupIds) {
          if (event.ticketLimitGroups[ticketTypeId]) {
            event.ticketLimitGroups[ticketTypeId]['regular'].groups.push({
              groupMax: group.remaining,
              groupQuantity,
              preventGroupQuantity,
              groupId: group.groupId,
              isPreventRule: group.isPreventRule,
              inCartLimit: group.inCartLimit,
              timeFrame: group.timeFrame,
              ruleType: group.ruleType,
              violation: group.violation,
            });
          }
        }
      }
    }

    if (Object.prototype.hasOwnProperty.call(event.memberGroupLimitMap, 'retail')) {
      const groups = event.memberGroupLimitMap.retail;

      for (const group of groups) {
        const ticketTypeIdList = group.groupId.split('-');
        const groupQuantity = this.getQuantityForTicketTypes(event.tickets, ticketTypeIdList, true);

        for (const ticketTypeId of ticketTypeIdList) {
          if (event.ticketLimitGroups[ticketTypeId]) {
            event.ticketLimitGroups[ticketTypeId]['retail'].groups.push({
              groupMax: group.remaining,
              groupQuantity,
              groupId: group.groupId,
              isPreventRule: group.isPreventRule,
              inCartLimit: group.inCartLimit,
              timeFrame: group.timeFrame,
              ruleType: group.ruleType,
            });
          }
        }
      }
    }
  }

  setMemberTicketLimitGroups(
    event: any,
    memberTicketLimitMap: Record<string, any>,
    limitMap: any,
    isRetail: boolean,
  ): void {
    const key = isRetail ? 'retail' : 'regular';
    const prices =
      event.event && event.event.priceList && event.event.priceList.prices ? event.event.priceList.prices : [];

    if (prices) {
      for (const ticket of prices) {
        if (!memberTicketLimitMap[ticket.personType.id]) {
          memberTicketLimitMap[ticket.personType.id] = {
            regular: {},
            retail: {},
          };
        }
        if (!memberTicketLimitMap[ticket.personType.id][key]) {
          memberTicketLimitMap[ticket.personType.id][key] = {};
        }

        if (
          limitMap &&
          limitMap[ticket.personType.id] &&
          limitMap[ticket.personType.id][key] &&
          limitMap[ticket.personType.id][key].groups instanceof Array
        ) {
          memberTicketLimitMap[ticket.personType.id][key].groups = limitMap[ticket.personType.id][key].groups;
        } else {
          memberTicketLimitMap[ticket.personType.id][key].groups = [];
        }
      }
    }
  }

  // ─── Event limit helpers ──────────────────────────────────────────────────

  eventHasLimitErrors(
    event: any,
    sameDayEventTicketQuantities?: Record<string, any>,
    onlyForSalesRules?: boolean,
  ): boolean {
    const prices = event.tickets || [];
    let hasErrors = false;

    // Accumulated by checkLimitExceeded() across all ticket types, so a trailing ticket type outside
    // a violated group doesn't reset the flags the group message templates read.
    event.groupLimitExceeded = false;
    event.groupSalesRuleRestricted = false;

    if (prices instanceof Array) {
      // Check every ticket (no early return): checkLimitExceeded() also sets the per-ticket
      // limitExceeded / salesRuleRestricted flags that the limit message templates read.
      for (const price of prices) {
        const limitExceeded = this.checkLimitExceeded(
          event,
          price,
          event.eventId,
          price.isRetail,
          sameDayEventTicketQuantities,
          false,
        );
        const salesRulesRestricted = this.checkLimitExceeded(
          event,
          price,
          event.eventId,
          price.isRetail,
          sameDayEventTicketQuantities,
          true,
        );

        hasErrors = hasErrors || (onlyForSalesRules ? salesRulesRestricted : limitExceeded);
      }
    }

    return hasErrors;
  }

  checkLimitExceeded(
    event: any,
    ticket: any,
    eventId: string,
    isRetail: boolean,
    sameDayEventTicketQuantities?: Record<string, any>,
    onlyForSalesRules?: boolean,
  ): boolean {
    let quantity: number;
    const type = isRetail ? 'retail' : 'regular';
    const key = isRetail ? ticket.ticketingTypeId + '-retail' : ticket.ticketingTypeId + '-regular';

    if (onlyForSalesRules) {
      quantity = this.getQuantityForTicketTypes(
        event.tickets,
        [ticket.ticketingTypeId],
        false,
        sameDayEventTicketQuantities,
      );
      quantity += this.getQuantityForTicketTypes(
        event.tickets,
        [ticket.ticketingTypeId],
        true,
        sameDayEventTicketQuantities,
      );
    } else {
      quantity = ticket.quantity;
    }

    if (sameDayEventTicketQuantities && sameDayEventTicketQuantities[key] !== undefined) {
      quantity = sameDayEventTicketQuantities[key].quantity;
    }

    // Per-ticket-type limits, split into member-benefit limits and event sales rules.
    let perTicketLimit = false;
    let salesRuleRestriction = false;
    const ticketLimit = event.memberTicketLimitMap?.[ticket.ticketingTypeId]?.[type];
    if (ticketLimit && ticketLimit.remaining !== null && quantity > ticketLimit.remaining) {
      salesRuleRestriction = !!ticketLimit.ruleType && ticketLimit.ruleType.name === this.ruleTypes[1].name;
      perTicketLimit = !!ticketLimit.ruleType && ticketLimit.ruleType.name === this.ruleTypes[0].name;
    }

    // Limits shared by several ticket types (e.g. "Adult + Cat combined, max 1"), built by
    // computeGroupTicketLimits(). An untyped group counts as a member-benefit limit.
    let groupLimit = false;
    let groupSalesRuleRestriction = false;
    const groups = event.ticketLimitGroups?.[ticket.ticketingTypeId]?.[type]?.groups;
    if (groups instanceof Array) {
      const ticketsQuantity = this.getTicketsQuantities(event.tickets);
      for (const group of groups) {
        if (group.groupMax === null || group.groupQuantity === null || !this.isGroupLimitViolated(group, ticketsQuantity)) {
          continue;
        }
        if (group.ruleType && group.ruleType.name === this.ruleTypes[1].name) {
          groupSalesRuleRestriction = true;
        } else if (!group.ruleType || group.ruleType.name === this.ruleTypes[0].name) {
          groupLimit = true;
        }
      }
    }

    event.groupLimitExceeded = !!event.groupLimitExceeded || groupLimit;
    event.groupSalesRuleRestricted = !!event.groupSalesRuleRestricted || groupSalesRuleRestriction;
    ticket.limitExceeded = perTicketLimit || groupLimit;
    ticket.salesRuleRestricted = salesRuleRestriction || groupSalesRuleRestriction;

    return onlyForSalesRules ? ticket.salesRuleRestricted : ticket.limitExceeded;
  }

  /**
   * Whether a ticket-type group (see computeGroupTicketLimits) is over its limit. Restores the group
   * check the pre-migration AngularJS SalesRulesService.checkLimitExceeded ran (DEV-14279).
   */
  private isGroupLimitViolated(group: any, ticketsQuantity: Record<string, number>): boolean {
    // Regular limit (member benefits and event sales): too many tickets across the group.
    if (!group.isPreventRule && group.groupQuantity > group.groupMax) {
      return true;
    }

    // Prevent rules need the underlying limit item; retail groups don't carry one.
    const violation = group.violation;
    if (!violation) {
      return false;
    }

    const violationTicketTypeIds: string[] = violation.ticketTypeIds?.length
      ? violation.ticketTypeIds
      : [violation.ticketTypeId];
    const preventRuleApplies =
      (group.isPreventRule && (violation.ticketTypeId || violation.ticketTypeIds?.length)) ||
      (group.inCartLimit && ticketsQuantity[violation.ticketTypeId] > violation.maxLimit) ||
      (!group.inCartLimit &&
        ticketsQuantity[violation.ticketTypeId] > violation.maxLimit - violation.purchasedQuantity);
    if (!preventRuleApplies) {
      return false;
    }

    const ticketsCounter = violationTicketTypeIds.reduce((sum, id) => sum + (ticketsQuantity[id] || 0), 0);
    const overLimit = group.groupMax < ticketsCounter || violation.maxLimit < violation.purchasedQuantity;

    return (
      overLimit &&
      (violation.validatedTicketTypeIds || []).some(
        (validatedId: string) => violationTicketTypeIds.indexOf(validatedId) < 0 && ticketsQuantity[validatedId] > 0,
      )
    );
  }

  // ─── Violation getters ────────────────────────────────────────────────────

  getViolationForTicketType(eventId: string, ticketTypeId: string, isRetail: boolean): any {
    const ruleType = this.getRuleType(eventId, ticketTypeId, isRetail);
    return {
      limit: this.getTicketLimit(eventId, ticketTypeId, isRetail),
      remaining: this.getRemainingLimit(eventId, ticketTypeId, isRetail, ruleType),
      purchased: this.getTicketPurchased(eventId, ticketTypeId, isRetail),
      timeFrame: this.getTimeFrame(eventId, ticketTypeId, isRetail),
      ruleType: this.getRuleType(eventId, ticketTypeId, isRetail),
      isPreventRule: this.isPreventRule(eventId, ticketTypeId, isRetail),
      inCartLimit: this.isInCartLimit(eventId, ticketTypeId, isRetail),
    };
  }

  getMemberTicketLimit(tickets: any[], eventId: string): any {
    const ticketLimitMap: Record<string, any> = {};
    let hasLimits = false;
    let hasSalesRuleRestriction = false;

    if (tickets instanceof Array) {
      for (const ticket of tickets) {
        ticketLimitMap[ticket.ticketTypeId] = {
          regular: this.getViolationForTicketType(eventId, ticket.ticketTypeId, false),
          retail: this.getViolationForTicketType(eventId, ticket.ticketTypeId, true),
        };

        if (!hasLimits && ticketLimitMap[ticket.ticketTypeId]) {
          hasLimits = !!(
            (ticketLimitMap[ticket.ticketTypeId]['regular'] &&
              ticketLimitMap[ticket.ticketTypeId]['regular'].limit !== null) ||
            (ticketLimitMap[ticket.ticketTypeId]['retail'] &&
              ticketLimitMap[ticket.ticketTypeId]['retail'].limit !== null)
          );
        }

        if (!hasSalesRuleRestriction && ticketLimitMap[ticket.ticketTypeId]) {
          hasSalesRuleRestriction = !!(
            (ticketLimitMap[ticket.ticketTypeId]['regular'] &&
              ticketLimitMap[ticket.ticketTypeId]['regular'].limit !== null) ||
            (ticketLimitMap[ticket.ticketTypeId]['retail'] &&
              ticketLimitMap[ticket.ticketTypeId]['retail'].limit !== null)
          );
        }
      }
    }

    return { ...ticketLimitMap, hasLimits, hasSalesRuleRestriction };
  }

  getMemberGroupLimit(tickets: any[], eventId: string): any {
    const groupLimitMap: any = { regular: [], retail: [] };
    let hasLimits = false;
    let hasSalesRuleRestriction = false;

    if (eventId && tickets instanceof Array && tickets.length) {
      groupLimitMap.regular = this.getGroupViolationsForEventWithNames(eventId, tickets, false) || [];
      groupLimitMap.retail = this.getGroupViolationsForEventWithNames(eventId, tickets, true) || [];

      // Member-benefit and event-sales-rule groups are mixed in the same arrays above (each
      // template filters by ruleType when rendering), but hasLimits/hasSalesRuleRestriction gate
      // two separate panels, so they must be derived per rule type rather than by array length --
      // otherwise a pure eventSales violation also flips hasLimits and renders an empty
      // "Member ticket limit" panel (and vice versa for a pure member-benefit violation).
      const allGroups = [...groupLimitMap.regular, ...groupLimitMap.retail];
      hasLimits = allGroups.some((group: any) => group.ruleType && group.ruleType.name !== this.ruleTypes[1].name);
      hasSalesRuleRestriction = allGroups.some(
        (group: any) => group.ruleType && group.ruleType.name === this.ruleTypes[1].name,
      );
    }

    return { ...groupLimitMap, hasLimits, hasSalesRuleRestriction };
  }

  // ─── Limit getters ────────────────────────────────────────────────────────

  getRemainingLimit(eventId: string, ticketTypeId: string, isRetail: boolean, ruleType: any): number | null {
    let r: number | null = null;
    isRetail = isRetail || false;

    if (Object.prototype.hasOwnProperty.call(this.limits, eventId)) {
      const items = this.limits[eventId].items;
      if (items instanceof Array && items.length) {
        for (let i = 0, ii = items.length; i < ii; i++) {
          const itemIsRetail = !!items[i].isRetail && true;

          if (ruleType === items[i].ruleType) {
            // if is salesrules
            if (items[i].ruleType === this.ruleTypes[1]) {
              if (
                items[i].validatedTicketTypeIds instanceof Array &&
                items[i].validatedTicketTypeIds.indexOf(ticketTypeId) > -1 &&
                itemIsRetail === isRetail
              ) {
                if (items[i].isPreventRule) {
                  r = 0;
                  break;
                } else {
                  r = items[i].inCartLimit ? items[i].maxLimit : items[i].maxLimit - items[i].purchasedQuantity;
                  if (r !== null && r < 0) {
                    r = 0;
                    break;
                  }
                }
              }

              // if is member rule
            } else {
              if (
                (items[i].ticketTypeId === ticketTypeId ||
                  (items[i].ticketTypeIds && items[i].ticketTypeIds.indexOf(ticketTypeId) > -1)) &&
                itemIsRetail === isRetail
              ) {
                r = items[i].inCartLimit ? items[i].maxLimit : items[i].maxLimit - items[i].purchasedQuantity;
                if (r !== null && r < 0) {
                  r = 0;
                  break;
                }
              }
            }
          }
        }
      }
    }

    return r;
  }

  getTicketLimit(eventId: string, ticketTypeId: string, isRetail: boolean): number | null {
    let r: number | null = null;
    isRetail = isRetail || false;

    if (Object.prototype.hasOwnProperty.call(this.limits, eventId)) {
      const items = this.limits[eventId].items;
      if (items instanceof Array && items.length) {
        for (let i = 0, ii = items.length; i < ii; i++) {
          const itemIsRetail = !!items[i].isRetail && true;

          if (items[i].ruleType === this.ruleTypes[1]) {
            if (
              items[i].validatedTicketTypeIds instanceof Array &&
              items[i].validatedTicketTypeIds.length <= 1 &&
              items[i].validatedTicketTypeIds.indexOf(ticketTypeId) > -1 &&
              itemIsRetail === isRetail
            ) {
              r = items[i].isPreventRule ? 0 : items[i].maxLimit;
              break;
            }
          } else {
            if (items[i].ticketTypeId === ticketTypeId && itemIsRetail === isRetail) {
              r = items[i].maxLimit;
              break;
            }
          }
        }
      }
    }
    return r;
  }

  getTicketPurchased(eventId: string, ticketTypeId: string, isRetail: boolean): number | null {
    let r: number | null = null;
    isRetail = isRetail || false;

    if (Object.prototype.hasOwnProperty.call(this.limits, eventId)) {
      const items = this.limits[eventId].items;
      if (items instanceof Array && items.length) {
        for (let i = 0, ii = items.length; i < ii; i++) {
          const itemIsRetail = !!items[i].isRetail && true;

          if (items[i].ruleType === this.ruleTypes[1]) {
            if (
              items[i].validatedTicketTypeIds instanceof Array &&
              items[i].validatedTicketTypeIds.length <= 1 &&
              items[i].validatedTicketTypeIds.indexOf(ticketTypeId) > -1 &&
              itemIsRetail === isRetail
            ) {
              r = items[i].purchasedQuantity;
              break;
            }
          } else {
            if (items[i].ticketTypeId === ticketTypeId && itemIsRetail === isRetail) {
              r = items[i].purchasedQuantity;
              break;
            }
          }
        }
      }
    }
    return r;
  }

  getTimeFrame(eventId: string, ticketTypeId: string, isRetail: boolean): TimeFrame | null {
    let r: TimeFrame | null = null;
    isRetail = isRetail || false;

    if (Object.prototype.hasOwnProperty.call(this.limits, eventId)) {
      const items = this.limits[eventId].items;
      if (items instanceof Array && items.length) {
        for (let i = 0, ii = items.length; i < ii; i++) {
          const itemIsRetail = !!items[i].isRetail && true;

          if (items[i].ruleType === this.ruleTypes[1]) {
            if (
              items[i].validatedTicketTypeIds instanceof Array &&
              items[i].validatedTicketTypeIds.length <= 1 &&
              items[i].validatedTicketTypeIds.indexOf(ticketTypeId) > -1 &&
              itemIsRetail === isRetail
            ) {
              r = items[i].timeFrame;
              break;
            }
          } else {
            if (items[i].ticketTypeId === ticketTypeId && itemIsRetail === isRetail) {
              r = items[i].timeFrame;
              break;
            }
          }
        }
      }
    }
    return r;
  }

  getRuleType(eventId: string, ticketTypeId: string, isRetail: boolean): RuleType | null {
    let r: RuleType | null = null;
    isRetail = isRetail || false;

    if (Object.prototype.hasOwnProperty.call(this.limits, eventId)) {
      const items = this.limits[eventId].items;
      if (items instanceof Array && items.length) {
        for (let i = 0, ii = items.length; i < ii; i++) {
          const itemIsRetail = !!items[i].isRetail && true;

          if (items[i].ruleType === this.ruleTypes[1]) {
            if (
              items[i].validatedTicketTypeIds instanceof Array &&
              items[i].validatedTicketTypeIds.length <= 1 &&
              items[i].validatedTicketTypeIds.indexOf(ticketTypeId) > -1 &&
              itemIsRetail === isRetail
            ) {
              r = items[i].ruleType;
              break;
            }
          } else {
            if (items[i].ticketTypeId === ticketTypeId && itemIsRetail === isRetail) {
              r = items[i].ruleType;
              break;
            }
          }
        }
      }
    }
    return r;
  }

  isPreventRule(eventId: string, ticketTypeId: string, isRetail: boolean): boolean | null {
    let r: boolean | null = null;
    isRetail = isRetail || false;

    if (Object.prototype.hasOwnProperty.call(this.limits, eventId)) {
      const items = this.limits[eventId].items;
      if (items instanceof Array && items.length) {
        for (let i = 0, ii = items.length; i < ii; i++) {
          const itemIsRetail = !!items[i].isRetail && true;

          if (items[i].ruleType === this.ruleTypes[1]) {
            if (
              items[i].validatedTicketTypeIds instanceof Array &&
              items[i].validatedTicketTypeIds.length <= 1 &&
              items[i].validatedTicketTypeIds.indexOf(ticketTypeId) > -1 &&
              itemIsRetail === isRetail
            ) {
              r = items[i].isPreventRule;
              break;
            }
          } else {
            r = false;
            break;
          }
        }
      }
    }
    return r;
  }

  isInCartLimit(eventId: string, ticketTypeId: string, isRetail: boolean): boolean | null {
    let r: boolean | null = null;
    isRetail = isRetail || false;

    if (Object.prototype.hasOwnProperty.call(this.limits, eventId)) {
      const items = this.limits[eventId].items;
      if (items instanceof Array && items.length) {
        for (let i = 0, ii = items.length; i < ii; i++) {
          const itemIsRetail = !!items[i].isRetail && true;

          if (items[i].ruleType === this.ruleTypes[1]) {
            if (
              items[i].validatedTicketTypeIds instanceof Array &&
              items[i].validatedTicketTypeIds.length <= 1 &&
              items[i].validatedTicketTypeIds.indexOf(ticketTypeId) > -1 &&
              itemIsRetail === isRetail
            ) {
              r = items[i].inCartLimit;
              break;
            }
          } else {
            r = false;
            break;
          }
        }
      }
    }
    return r;
  }

  // ─── Processing ───────────────────────────────────────────────────────────

  processSalesRulesLimits(limitsData: any[]): SalesRulesLimits {
    this.resetLimits();

    if (limitsData instanceof Array) {
      for (let i = 0, ii = limitsData.length; i < ii; i++) {
        let pricePointIds: string[] = [];
        let validatedPricePointIds: string[] = [];
        let pricePointId: string | null = null;
        let ruleType: RuleType = this.ruleTypes[0];
        let isPreventRule = false;
        let timeFrame: TimeFrame | null = null;

        const allowedQuantity = limitsData[i].allowedQuantity;
        const purchasedQuantity = limitsData[i].purchasedQuantity || 0;
        const inCartLimit = limitsData[i].reason === this.reasonTypes[0].name || false;
        const isRetail = !!limitsData[i].isRetail && true;
        if (limitsData[i].timeFrame) {
          timeFrame = this.findTimeFrame(limitsData[i].timeFrame);
        }

        if (
          Object.prototype.hasOwnProperty.call(limitsData[i], 'pricePointIds') &&
          limitsData[i].pricePointIds instanceof Array
        ) {
          if (limitsData[i].pricePointIds.length > 1) {
            pricePointIds = limitsData[i].pricePointIds;
          } else if (limitsData[i].pricePointIds.length === 1) {
            pricePointId = limitsData[i].pricePointIds[0];
          }
        } else if (Object.prototype.hasOwnProperty.call(limitsData[i], 'pricePointId')) {
          pricePointId = limitsData[i].pricePointId;
        }

        if (
          Object.prototype.hasOwnProperty.call(limitsData[i], 'validatedPricePointIds') &&
          limitsData[i].validatedPricePointIds instanceof Array
        ) {
          validatedPricePointIds = limitsData[i].validatedPricePointIds;
        }

        const eventId = limitsData[i].eventId;

        ruleType =
          limitsData[i].ruleType && limitsData[i].ruleType === this.ruleTypes[1].name
            ? this.ruleTypes[1]
            : this.ruleTypes[0];
        // if this is an event sales rule and the pricepoints being purchased are the same as the pricepoints that are limited, then this is not a prevent rule
        if (
          ruleType === this.ruleTypes[1] &&
          !this.arePricePointsEqual(
            pricePointIds.length ? pricePointIds : pricePointId ? [pricePointId] : [],
            validatedPricePointIds || [],
          )
        ) {
          isPreventRule = true;
        }

        if (allowedQuantity !== undefined && allowedQuantity !== null && eventId) {
          if (!Object.prototype.hasOwnProperty.call(this.limits, eventId)) {
            this.limits[eventId] = { items: [] };
          }
          this.limits[eventId].items.push({
            maxLimit: allowedQuantity,
            purchasedQuantity,
            ticketTypeId: pricePointId,
            ticketTypeIds: pricePointIds,
            // this is a hack to consolidate prevented ticket types within the same cart
            validatedTicketTypeIds: inCartLimit
              ? validatedPricePointIds.concat(pricePointId ? [pricePointId] : pricePointIds)
              : validatedPricePointIds,
            isRetail,
            timeFrame,
            ruleType,
            isPreventRule,
            inCartLimit,
          });
        }
      }
    }
    return this.limits;
  }

  // ─── Group violations ─────────────────────────────────────────────────────

  getGroupViolationsForEvent(eventId: string, isRetail: boolean): any[] | null {
    const groups: any[] = [];
    isRetail = isRetail || false;

    if (Object.prototype.hasOwnProperty.call(this.limits, eventId)) {
      const items = this.limits[eventId].items;
      if (items instanceof Array && items.length) {
        for (let i = 0, ii = items.length; i < ii; i++) {
          const itemIsRetail = !!items[i].isRetail && true;

          const remaining = items[i].inCartLimit
            ? items[i].maxLimit
            : Math.max(items[i].maxLimit - (items[i].purchasedQuantity || 0), 0);

          if (items[i].ruleType === this.ruleTypes[1]) {
            if (
              items[i].validatedTicketTypeIds instanceof Array &&
              items[i].validatedTicketTypeIds.length > 1 &&
              itemIsRetail === isRetail
            ) {
              const groupIds: string[] = items[i].validatedTicketTypeIds.slice();

              if (
                items[i].ticketTypeId &&
                groupIds.indexOf(items[i].ticketTypeId) < 0 &&
                !(items[i].isPreventRule && items[i].purchasedQuantity > 0)
              ) {
                groupIds.push(items[i].ticketTypeId);
              }

              groups.push({
                limit: items[i].isPreventRule ? 0 : items[i].maxLimit,
                remaining,
                purchased: items[i].purchasedQuantity,
                groupId: groupIds.join('-'),
                timeFrame: items[i].timeFrame,
                ruleType: items[i].ruleType,
                isPreventRule: items[i].isPreventRule,
                inCartLimit: items[i].inCartLimit,
                violation: items[i],
              });
            }
          } else {
            if (items[i].ticketTypeIds instanceof Array && items[i].ticketTypeIds.length && itemIsRetail === isRetail) {
              groups.push({
                limit: items[i].maxLimit,
                remaining,
                purchased: items[i].purchasedQuantity,
                groupId: items[i].ticketTypeIds.join('-'),
                timeFrame: items[i].timeFrame,
                ruleType: items[i].ruleType,
                isPreventRule: items[i].isPreventRule,
                inCartLimit: items[i].inCartLimit,
                violation: items[i],
              });
            }
          }
        }
      }
    }

    return groups.length ? groups : null;
  }

  getGroupViolationsForEventWithNames(eventId: string, tickets: any[], isRetail: boolean): any[] | null {
    const violations = this.getGroupViolationsForEvent(eventId, isRetail);
    if (violations instanceof Array) {
      violations.forEach((violation) => {
        const ticketTypeIds = violation.groupId ? violation.groupId.split('-') : [];
        const names = this.getNamesForTicketTypes(ticketTypeIds, tickets);
        violation.ticketTypes = names.length ? names.join(', ') : '';
      });
    }

    return violations;
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  arePricePointsEqual(src: any[], dest: any[]): boolean {
    if (src instanceof Array && dest instanceof Array) {
      return src.join('') === dest.join('');
    }
    return false;
  }

  getNamesForTicketTypes(ticketTypeIds: string[], tickets: any[]): string[] {
    const names: string[] = [];

    if (tickets instanceof Array && ticketTypeIds instanceof Array) {
      // Each type once: a group id can repeat types (processSalesRulesLimits appends the rule's
      // price points to the validated ones for in-cart limits). The pre-migration service
      // de-duplicated here too; without it the message reads "Adult, Cat, Adult, Cat".
      for (const ticketTypeId of new Set(ticketTypeIds)) {
        const found = tickets.filter((ticket) => ticket.ticketTypeId === ticketTypeId);

        if (found && found.length) {
          names.push(found[0].name || found[0].description || '');
        }
      }
    }

    return names;
  }

  getIgnoreTicketLimits(event: any): boolean {
    const tickets = event.tickets || [];

    for (const ticket of tickets) {
      if (ticket.ignoreEntitlements) {
        return true;
      }
    }

    return false;
  }
}
